import React from 'react';
import PropTypes from 'prop-types';

import { useConfig } from 'Context/ConfigContext';
import { secondsToTime } from 'Util/datetime';

import { Box, Flex, Text, SvgIcon } from '../../Base';

export const QUIZ_DETAIL_VARIANTS = {
  LANDING: 'LANDING',
  RESULT: 'RESULT',
};

const DetailText = ({ children, ...props }) => (
  <Text fontSize={{ _: 1, sm: 2, lg: 3 }} {...props}>
    {children}
  </Text>
);

const getTimeLimitText = (timeLimit) => {
  const minutes = Math.floor(timeLimit / 60);
  const second = timeLimit % 60;
  return `${minutes} นาที ${second ? `${second} วินาที` : ''}`;
};

const QuizDetails = ({ variant, data }) => {
  const {
    classroom: { quiz = {} },
  } = useConfig();
  const { showEstimatedTime = true, gradedExam = {} } = quiz;
  const { showPassingCriteria = true } = gradedExam;
  const { passingScore, fullScore, studentScore, timeLimit, timeSpent } = data ?? {};

  const renderFirstRow = () => {
    switch (variant) {
      case QUIZ_DETAIL_VARIANTS.RESULT: {
        const studentScoreText = `${studentScore}/${fullScore}`;
        return (
          <>
            <Flex alignItems={'center'} width={2 / 3}>
              <SvgIcon icon={SvgIcon.ICON_LIST.MEDAL} />
              <DetailText ml={3}>คะแนนสอบของคุณ</DetailText>
            </Flex>
            <Box width={1 / 3}>
              {studentScoreText && (
                <Box maxWidth={{ _: 100, sm: 150 }}>
                  <DetailText>{studentScoreText}</DetailText>
                </Box>
              )}
            </Box>
          </>
        );
      }
      case QUIZ_DETAIL_VARIANTS.LANDING:
      default: {
        const passingScoreText = `${passingScore}/${fullScore}`;
        const passingScorePercentText = `${Math.ceil((passingScore / fullScore) * 100)}%`;

        return (
          showPassingCriteria && (
            <>
              <Flex alignItems={'center'} width={{ _: 4 / 7, md: 2 / 3 }}>
                <SvgIcon icon={SvgIcon.ICON_LIST.MEDAL} />
                <DetailText ml={3}>เกณฑ์คะแนนผ่าน</DetailText>
              </Flex>
              <Box width={{ _: 3 / 7, md: 1 / 3 }}>
                {passingScoreText && passingScorePercentText && (
                  <Flex maxWidth={{ _: 100, sm: 150 }}>
                    <DetailText pr={3}>{passingScorePercentText}</DetailText>
                    <DetailText pr={3}>|</DetailText>
                    <DetailText>{passingScoreText}</DetailText>
                  </Flex>
                )}
              </Box>
            </>
          )
        );
      }
    }
  };

  const renderSecondRow = () => {
    switch (variant) {
      case QUIZ_DETAIL_VARIANTS.RESULT:
        return (
          <>
            <Flex width={2 / 3} alignItems={'center'}>
              <SvgIcon icon={SvgIcon.ICON_LIST.GRADED_EXAM_TIMER} />
              <DetailText ml={3}>เวลาที่ใช้ในการสอบ</DetailText>
            </Flex>
            <Box width={1 / 3}>
              {typeof timeSpent === 'number' && (
                <DetailText>{secondsToTime(timeSpent, { autoHideHour: true })}</DetailText>
              )}
            </Box>
          </>
        );
      case QUIZ_DETAIL_VARIANTS.LANDING:
        return (
          showEstimatedTime && (
            <>
              <Flex alignItems={'center'} width={{ _: 4 / 7, md: 2 / 3 }}>
                <SvgIcon icon={SvgIcon.ICON_LIST.GRADED_EXAM_TIMER} />
                <DetailText ml={3}>ระยะเวลาในการสอบ</DetailText>
              </Flex>
              <Box width={{ _: 3 / 7, md: 1 / 3 }}>
                {timeLimit && <DetailText>{getTimeLimitText(timeLimit)}</DetailText>}
              </Box>
            </>
          )
        );
      default:
        return null;
    }
  };

  let pb = 0;
  if (showEstimatedTime || showPassingCriteria) pb = 7;

  return (
    <Box width={1} maxWidth={{ _: 320, md: 450 }} pb={{ _: 2, md: pb }}>
      <Flex width={1} pb={2}>
        {renderFirstRow()}
      </Flex>
      <Flex width={1}>{renderSecondRow()}</Flex>
    </Box>
  );
};

QuizDetails.propTypes = {
  data: PropTypes.shape({
    passingScore: PropTypes.number,
    fullScore: PropTypes.number,
    studentScore: PropTypes.number,
    timeLimit: PropTypes.number,
    timeSpent: PropTypes.number,
  }),
  variant: PropTypes.string,
};

export default QuizDetails;
